import { useCallback, useEffect, useRef, useState } from 'react'
import type { TangerLayerSpec } from '../map/tileUrl'

export interface HeatmapResponse {
  status: 'PENDING' | 'OUTDATED' | 'UP-TO-DATE'
  layer?: TangerLayerSpec | null
  // The fraction (0..1) of the search area covered by the current search
  // trails. Absent or null means no figure is available yet — the caller
  // renders nothing in that case, never a 0.0% placeholder.
  coverage?: number | null
}

export type HeatmapPhase = 'loading' | 'updating' | 'ready' | 'stalled' | 'error'

const FAST_INTERVAL_MS = 5_000
const SLOW_INTERVAL_MS = 15_000
const FAST_WINDOW_MS = 120_000
const GIVE_UP_MS = 900_000

/**
 * Drives the heatmap layer to a current one by polling `/api/heatmap`.
 *
 * The endpoint answers UP-TO-DATE, OUTDATED (a usable but stale layer exists
 * while a fresher one is being built) or PENDING (nothing to show yet). The
 * two latter cases keep polling; an outdated layer is rendered meanwhile,
 * because a slightly stale heatmap beats an empty screen. Polling starts at
 * a 5s cadence, slows to 15s after two minutes, and gives up after fifteen
 * minutes of actual polling time (see below for what "actual" excludes).
 *
 * The fetch is injected so the schedule can be driven by fake timers in a
 * test without a network or an auth provider.
 */
export function useHeatmapLayer(fetchLayer: () => Promise<HeatmapResponse>) {
  const [phase, setPhase] = useState<HeatmapPhase>('loading')
  const [layer, setLayer] = useState<TangerLayerSpec | null>(null)
  const [coverage, setCoverage] = useState<number | null>(null)
  const [attempt, setAttempt] = useState(0)

  // fetchLayer is not a dependency of the effect below, for the same reason
  // useAccess/useBaseMap read `call` through a ref: useApiFetch's returned
  // function is a useCallback keyed on live auth state, so its identity
  // changes on every token refresh. If the effect re-ran on every such
  // change, the poll schedule would restart from zero each time — silently
  // resetting the two-minute/fifteen-minute clocks on an ordinary token
  // refresh, not just on a deliberate retry. Keying the effect on `attempt`
  // alone, and always reading the latest `fetchLayer` through this ref,
  // decouples "when to (re)start polling" (mount + explicit retry) from
  // "which function instance to call" (always the newest one).
  const fetchLayerRef = useRef(fetchLayer)
  fetchLayerRef.current = fetchLayer

  useEffect(() => {
    let cancelled = false
    let inFlight = false
    let timer: ReturnType<typeof setTimeout> | null = null
    // Mirrors `phase` synchronously inside this closure. React state is only
    // visible to the next render, but the visibility handler below needs to
    // know *right now* whether polling is still in progress, so it reads
    // this instead of the `phase` state variable.
    let currentPhase: HeatmapPhase = 'loading'

    const startedAt = Date.now()
    // Time spent with the tab hidden is excluded from both the 2-minute
    // fast/slow cutover and the 15-minute give-up clock: nothing was
    // actually polling while backgrounded, so it isn't time the server
    // spent failing to answer. A tab backgrounded for ten minutes and then
    // refocused should not be greeted with "still processing" the instant
    // it comes back — that would blame the server for a wait the user
    // caused by looking away, not one the server caused by being slow.
    // hiddenSince/hiddenMs track how much wall-clock time to subtract.
    let hiddenSince: number | null = document.visibilityState === 'hidden' ? Date.now() : null
    let hiddenMs = 0

    function updatePhase(next: HeatmapPhase) {
      currentPhase = next
      setPhase(next)
    }

    function clearTimer() {
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
    }

    function elapsedActiveMs() {
      const hiddenNow = hiddenSince !== null ? Date.now() - hiddenSince : 0
      return Date.now() - startedAt - hiddenMs - hiddenNow
    }

    async function poll() {
      inFlight = true
      let response: HeatmapResponse
      try {
        response = await fetchLayerRef.current()
      } catch {
        inFlight = false
        if (!cancelled) updatePhase('error')
        return
      }
      inFlight = false
      if (cancelled) return

      if (response.layer) {
        // response.layer is a fresh object from res.json() on every poll,
        // even when Tanger reports the SAME layer while a fresher one is
        // still building. Minting a new reference here every 5s during the
        // OUTDATED window would key MapView's [heatmap] effect to re-run on
        // every poll — re-adding the same source and re-downloading its
        // tiles for up to 15 minutes. Keep the previous reference whenever
        // the layer's identity (url + generation) hasn't actually changed.
        const next = response.layer
        setLayer((prev) =>
          prev && prev.url === next.url && prev.generation === next.generation ? prev : next,
        )
      }

      // Tracked independently of layer: coverage is a plain number, so there
      // is no identity to preserve, and it survives a PENDING poll that
      // carries no layer at all.
      setCoverage(response.coverage ?? null)

      if (response.status === 'UP-TO-DATE') {
        updatePhase('ready')
        return
      }

      if (elapsedActiveMs() >= GIVE_UP_MS) {
        updatePhase('stalled')
        return
      }

      updatePhase('updating')

      // A hidden tab has nobody watching the map: don't schedule the next
      // poll now. onVisibilityChange fires one off immediately once the tab
      // is visible again, so nothing is missed, but nothing fires while
      // backgrounded either.
      if (document.visibilityState === 'hidden') return

      const interval = elapsedActiveMs() < FAST_WINDOW_MS ? FAST_INTERVAL_MS : SLOW_INTERVAL_MS
      timer = setTimeout(() => void poll(), interval)
    }

    void poll()

    function onVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        hiddenSince = Date.now()
        clearTimer()
        return
      }

      if (hiddenSince !== null) {
        hiddenMs += Date.now() - hiddenSince
        hiddenSince = null
      }

      // Resume immediately rather than waiting out whatever interval was
      // pending when the tab went hidden — but only while the machine is
      // still trying to reach a current layer. A settled ready/stalled/error
      // phase must not restart polling just because the tab regained focus.
      const isPolling = currentPhase === 'loading' || currentPhase === 'updating'
      if (!cancelled && !inFlight && timer === null && isPolling) {
        void poll()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      cancelled = true
      clearTimer()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])
  return { phase, layer, coverage, retry }
}
